import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { PartnerService } from '@nimbus-admin/features/partner/services/partner.service';
import { TenantApiClient } from '@nimbus-admin/features/tenant/services/tenant.api-client';
import { TenantService } from '@nimbus-admin/features/tenant/services/tenant.service';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import { PermissionsModel } from '@nimbus-global/features/permissions/models/permissions.model';
import { sortByLocale } from '@nimbus-library/features/localization/utils/active-locale';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';

export interface ITenantSelectionScope {
  isPartnerRequired: boolean;
  tenantIds: string[];
  loadOwnTenants: boolean;
  canSelectTenant: boolean;
}

export interface ITenantSelection {
  partnerId?: string;
}

@Injectable({ providedIn: 'root' })
export class GroupSelectionLookupService {
  public get isPartnerRequired(): boolean {
    return this.getTenantSelectionScope().isPartnerRequired;
  }

  public constructor(
    private readonly partnerService: PartnerService,
    private readonly tenantService: TenantService,
    private readonly permissionManager: IPermissionManagerService,
    private readonly tenantApiClient: TenantApiClient,
  ) {}

  public getTenantSelectionScope(permissions: PermissionsModel = this.permissionManager.getPermissionsModel()): ITenantSelectionScope {
    const isPartnerRequired = permissions.hasPermissionSet(NimbusPermissionSet.NimbusAdmin);
    const loadOwnTenants = !isPartnerRequired && permissions.hasPermissionSet(NimbusPermissionSet.PartnerAdminForTenant);
    return {
      isPartnerRequired,
      loadOwnTenants,
      tenantIds: loadOwnTenants
        ? permissions.tenantIds.filter((id) => permissions.hasTenantPermissionSet(id, NimbusPermissionSet.PartnerAdminForTenant))
        : permissions.tenantIds,
      canSelectTenant:
        isPartnerRequired ||
        loadOwnTenants ||
        [
          NimbusPermissionSet.TenantAdmin,
          NimbusPermissionSet.PartnerAdmin,
          NimbusPermissionSet.OuAdmin,
          NimbusPermissionSet.WorkflowAdminLimited,
        ].some((role) => permissions.hasPermissionSet(role)) ||
        permissions.hasCustomRole(),
    };
  }

  public getPartners(): Observable<IOptionItem[]> {
    return this.isPartnerRequired
      ? this.partnerService.getPartners().pipe(
          map((partners) =>
            sortByLocale(partners, (partner): string => partner.name || partner.o365Domain).map((partner) => ({
              id: partner.id,
              name: partner.name || partner.o365Domain,
            })),
          ),
        )
      : of([]);
  }

  public getTenants(partnerId: string, permissions?: PermissionsModel): Observable<IOptionItem[]> {
    const scope = this.getTenantSelectionScope(permissions);
    if (scope.isPartnerRequired && !partnerId) {
      return of([]);
    }
    if (!scope.isPartnerRequired && !scope.tenantIds.length) {
      return of([]);
    }
    const tenants$ = scope.isPartnerRequired ? this.tenantService.getTenants(partnerId) : this.tenantService.getOwnTenants(scope.tenantIds);
    return tenants$.pipe(
      map((tenants) =>
        sortByLocale(tenants, (tenant): string => tenant.name || tenant.o365Domain).map((tenant) => ({
          id: tenant.id,
          name: tenant.name || tenant.o365Domain,
        })),
      ),
    );
  }

  public getTenantSelection(tenantId: string): Observable<ITenantSelection | null> {
    if (!tenantId) {
      return of(null);
    }

    return this.permissionManager.getPermissionsModel$().pipe(
      filter((permissions) => !!permissions && permissions !== PermissionsModel.Empty),
      take(1),
      switchMap((permissions) => {
        const scope = this.getTenantSelectionScope(permissions);
        if (!scope.canSelectTenant || (!scope.isPartnerRequired && !scope.tenantIds.includes(tenantId))) {
          return of(null);
        }
        if (!scope.isPartnerRequired) {
          return of({});
        }
        return this.getTenantPartnerIdIfAvailable(tenantId).pipe(map((partnerId) => (partnerId ? { partnerId } : {})));
      }),
    );
  }

  public getTenantOptions(tenantId: string): Observable<IOptionItem[]> {
    if (!tenantId) {
      return of([]);
    }
    return this.isPartnerRequired
      ? this.getTenantPartnerId(tenantId).pipe(switchMap((partnerId) => this.getTenants(partnerId)))
      : this.getTenants('');
  }

  public getTenantPartnerId(tenantId: string): Observable<string> {
    return this.getTenantPartnerIdIfAvailable(tenantId).pipe(
      map((partnerId) => {
        if (!partnerId) {
          throw new Error('Tenant partner unavailable');
        }
        return partnerId;
      }),
    );
  }

  private getTenantPartnerIdIfAvailable(tenantId: string): Observable<string | null> {
    return this.tenantApiClient.getTenantDetails(tenantId).pipe(map((tenant) => tenant?.partner?.id || null));
  }
}
